import { useMemo } from 'react';

import { Communication, CommunicationHistoryResponse } from '@/types/communication';

import { useGetCallHistory, VendorCall } from './useGetCallHistory';

interface CommunicationHistoryParams {
  searchQuery?: string;
  limit?: number;
  offset?: number;
}

export const useGetCommunicationHistory = (
  params?: CommunicationHistoryParams,
  enabled = true
) => {
  // Fetch unified communication history (calls and SMS) from the enhanced call history endpoint
  const {
    data: callHistory,
    isLoading: isLoadingCommunications,
    error: communicationError,
  } = useGetCallHistory(
    params ? { ...params, communicationType: 'all' } : { communicationType: 'all' },
    enabled
  );

  // Transform unified data to the expected format
  const combinedData = useMemo((): CommunicationHistoryResponse | undefined => {
    if (!callHistory?.calls) return undefined;

    const communications: Communication[] = callHistory.calls.map((item: VendorCall) => {
      const getId = () => {
        if (item.type === 'call') {
          return item.call_sid || item.id.toString();
        }
        if (item.type === 'sms_conversation') {
          return item.primary_message_sid || item.id.toString();
        }
        return item.message_sid || item.id.toString();
      };

      const baseComm = {
        id: getId(),
        vendor_name: item.vendor_name,
        phone_number: item.phone_number,
        search_query: item.search_query,
        property_address: item.property_address,
        urgency: item.urgency,
        requirements: item.requirements,
        status: item.status,
        has_quote: item.has_quote,
        created_at: item.created_at,
        updated_at: item.updated_at,
        quote_summary: item.quote_summary,
      };

      if (item.type === 'call') {
        return {
          ...baseComm,
          type: 'call' as const,
          call_sid: item.call_sid || item.id.toString(),
          summarized_transcript: item.summarized_transcript,
          is_voicemail: item.is_voicemail,
          voicemail_message_left: item.voicemail_message_left,
          call_ended_reason: item.call_ended_reason,
          has_transcript: item.has_transcript,
          has_recording: item.has_recording || false,
          audio_url: item.audio_url,
        };
      } 
      
      if (item.type === 'sms_conversation') {
        // Handle grouped SMS conversation (multiple vendors/messages per search)
        return {
          ...baseComm,
          type: 'sms' as const,
          message_sid: item.primary_message_sid || item.id.toString(),
          conversation_summary: item.summarized_transcript,
          message_content: item.conversation_summary ? 
            item.conversation_summary.map(msg => `${msg.vendor} (${msg.direction}): ${msg.content}`).join('\n\n') : 
            undefined,
          direction: item.outbound_messages && item.outbound_messages > 0 ? 'outbound' : 'inbound',
        };
      }
      
      // Handle individual SMS (legacy SMS without search_id)
      return {
        ...baseComm,
        type: 'sms' as const,
        message_sid: item.message_sid || item.id.toString(),
        conversation_summary: item.summarized_transcript,
        message_content: item.message_content,
        direction: item.direction || 'outbound',
      };
      
    });

    const callCount = communications.filter(c => c.type === 'call').length;
    const smsCount = communications.filter(c => c.type === 'sms').length;
    const quoteCount = communications.filter(c => 
      c.quote_summary && (
        (c.quote_summary.pricing_info && c.quote_summary.pricing_info.length > 0) ||
        c.quote_summary.price_range ||
        c.quote_summary.available
      )
    ).length;

    return {
      communications,
      total_count: callHistory.total_count,
      call_count: callCount,
      sms_count: smsCount,
      quote_count: quoteCount,
      has_more: callHistory.has_more,
    };
  }, [callHistory]);

  return {
    data: combinedData,
    isLoading: isLoadingCommunications,
    error: communicationError,
  };
};